import { openPopoutWindow } from 'components/common/popoutWindow';
import { AUTH_REQUEST_MESSAGE, NONCE_PATTERN, createNonce } from 'services/alpha/popupAuth';
import { CardDraft } from 'components/Alpha/cards/cardDraft';

const MARKER = 'alphaVoice';
const SESSION_PARAM = 'alphaSession';
const VOICE_VALUE = 'popup';
const DICTATION_VALUE = 'dictation';
const VOICE_POPUP_NAME = 'mspp-alpha-voice';
const DICTATION_POPUP_NAME = 'mspp-alpha-dictation';
const START_KEY = 'alphaStart';

export const START_CHALLENGE_MESSAGE = 'mspp-alpha-start-challenge';
export const START_CONFIRM_MESSAGE = 'mspp-alpha-start-confirm';
export const START_CHALLENGE_TIMEOUT_MS = 1000;
export const START_NONCE_LIFETIME_MS = 60000;

export const VOICE_CLOSED_MESSAGE = 'mspp-alpha-voice-closed';
export const DICTATION_MESSAGE = 'mspp-alpha-dictation';

export type PopupDrafts = Record<string, CardDraft>;

export type AlphaPopupMessage =
  | { type: typeof VOICE_CLOSED_MESSAGE; sessionId: number; drafts?: PopupDrafts }
  | { type: typeof DICTATION_MESSAGE; text: string }
  | { type: typeof AUTH_REQUEST_MESSAGE; nonce: string }
  | { type: typeof START_CHALLENGE_MESSAGE; nonce: string };

const markerValue = (): string | null => {
  try {
    return new URLSearchParams(window.location.search).get(MARKER);
  } catch {
    return null;
  }
};

export const isAlphaVoicePopupView = (): boolean => markerValue() === VOICE_VALUE;

export const isAlphaDictationPopupView = (): boolean => markerValue() === DICTATION_VALUE;

export const isAlphaPopupWindow = (): boolean =>
  isAlphaVoicePopupView() || isAlphaDictationPopupView();

const toSessionId = (raw: string | null | undefined): number | undefined => {
  if (!raw || !/^\d{1,15}$/.test(raw)) return undefined;
  const value = Number(raw);
  return Number.isSafeInteger(value) && value > 0 ? value : undefined;
};

export const getAlphaPopupSessionId = (): number | undefined => {
  try {
    return toSessionId(new URLSearchParams(window.location.search).get(SESSION_PARAM));
  } catch {
    return undefined;
  }
};

const MAX_POPUP_DRAFTS = 20;
const MAX_TOKEN_LENGTH = 512;
export const MAX_POPUP_DRAFT_BYTES = 64 * 1024;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const wholeOrUndefined = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;

const withinDraftCap = (values: unknown): boolean => {
  try {
    const serialized = JSON.stringify(values);
    return (
      typeof serialized === 'string' &&
      new TextEncoder().encode(serialized).length <= MAX_POPUP_DRAFT_BYTES
    );
  } catch {
    return false;
  }
};

const toPopupDrafts = (raw: unknown): PopupDrafts | undefined => {
  if (!isRecord(raw)) return undefined;
  const entries = Object.entries(raw)
    .filter(
      ([token, draft]) =>
        token.length > 0 &&
        token.length <= MAX_TOKEN_LENGTH &&
        isRecord(draft) &&
        isRecord(draft.values) &&
        withinDraftCap(draft.values),
    )
    .slice(0, MAX_POPUP_DRAFTS)
    .map(([token, draft]): [string, CardDraft] => {
      const value = draft as Record<string, unknown>;
      return [
        token,
        {
          values: value.values as Record<string, unknown>,
          step: wholeOrUndefined(value.step),
          furthest: wholeOrUndefined(value.furthest),
        },
      ];
    });
  return entries.length ? Object.fromEntries(entries) : undefined;
};

const openedPopups = new Set<Window>();

const issuedStartNonces = new Map<string, { popup: Window; expiresAt: number }>();

const forgetStartNonces = (popup: Window): void => {
  const now = Date.now();
  issuedStartNonces.forEach((issued, nonce) => {
    if (issued.popup === popup || issued.expiresAt <= now) issuedStartNonces.delete(nonce);
  });
};

const openMarked = (value: string, name: string, sessionId?: number): Window | null => {
  const url = new URL(window.location.href);
  url.searchParams.set(MARKER, value);
  url.searchParams.delete(SESSION_PARAM);
  if (sessionId !== undefined && toSessionId(String(sessionId))) {
    url.searchParams.set(SESSION_PARAM, String(sessionId));
  }
  const nonce = createNonce();
  url.hash = `${START_KEY}=${nonce}`;
  const popup = openPopoutWindow({ url: url.toString(), name });
  if (popup) {
    openedPopups.add(popup);
    forgetStartNonces(popup);
    issuedStartNonces.set(nonce, { popup, expiresAt: Date.now() + START_NONCE_LIFETIME_MS });
  }
  return popup;
};

export const answerStartChallenge = (nonce: string, source: Window): boolean => {
  const issued = issuedStartNonces.get(nonce);
  if (!issued || issued.popup !== source) return false;
  issuedStartNonces.delete(nonce);
  if (issued.expiresAt <= Date.now()) return false;
  source.postMessage({ type: START_CONFIRM_MESSAGE, nonce }, window.location.origin);
  return true;
};

export const readStartNonce = (): string | undefined => {
  try {
    const nonce = new URLSearchParams(window.location.hash.replace(/^#/, '')).get(START_KEY);
    return nonce && NONCE_PATTERN.test(nonce) ? nonce : undefined;
  } catch {
    return undefined;
  }
};

export const clearStartNonceFromAddress = (): boolean => {
  try {
    if (!window.location.hash) return false;
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${window.location.search}`,
    );
    return true;
  } catch {
    return false;
  }
};

export const openAlphaVoicePopup = (sessionId?: number): Window | null =>
  openMarked(VOICE_VALUE, VOICE_POPUP_NAME, sessionId);

export const openAlphaDictationPopup = (sessionId?: number): Window | null =>
  openMarked(DICTATION_VALUE, DICTATION_POPUP_NAME, sessionId);

export const openedBySameOrigin = (): boolean => {
  try {
    const { opener } = window;
    if (!opener || opener === window || opener.closed) return false;
    return (opener as Window).location.origin === window.location.origin;
  } catch {
    return false;
  }
};

export const verifyStartWithOpener = (
  nonce: string | undefined,
  timeoutMs = START_CHALLENGE_TIMEOUT_MS,
): Promise<boolean> =>
  new Promise(resolve => {
    if (!nonce || !openedBySameOrigin()) {
      resolve(false);
      return;
    }
    const opener = window.opener as Window;
    let timer = 0;
    const finish = (verified: boolean): void => {
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      resolve(verified);
    };
    function onMessage (event: MessageEvent): void {
      if (event.origin !== window.location.origin || event.source !== opener) return;
      const data = event.data as { type?: unknown; nonce?: unknown } | null;
      const confirmed =
        Boolean(data) &&
        typeof data === 'object' &&
        data?.type === START_CONFIRM_MESSAGE &&
        data?.nonce === nonce;
      if (confirmed) finish(true);
    }
    window.addEventListener('message', onMessage);
    timer = window.setTimeout(() => {
      finish(false);
    }, timeoutMs);
    try {
      opener.postMessage({ type: START_CHALLENGE_MESSAGE, nonce }, window.location.origin);
    } catch {
      finish(false);
    }
  });

export const postToOpener = (message: AlphaPopupMessage): boolean => {
  try {
    const { opener } = window;
    if (!opener || opener === window || opener.closed) return false;
    opener.postMessage(message, window.location.origin);
    return true;
  } catch {
    return false;
  }
};

export const closeAlphaPopupWindow = (): void => {
  try {
    window.close();
  } catch {
    /* the browser refused to close it */
  }
};

export const parseAlphaPopupMessage = (event: {
  origin: string;
  source: unknown;
  data: unknown;
}): AlphaPopupMessage | null => {
  if (event.origin !== window.location.origin) return null;
  if (!event.source || !openedPopups.has(event.source as Window)) return null;
  const data = event.data as {
    type?: unknown;
    sessionId?: unknown;
    text?: unknown;
    drafts?: unknown;
  } | null;
  if (!data || typeof data !== 'object') return null;
  if (data.type === VOICE_CLOSED_MESSAGE && typeof data.sessionId === 'number') {
    if (!toSessionId(String(data.sessionId))) return null;
    const drafts = toPopupDrafts(data.drafts);
    return drafts
      ? { type: VOICE_CLOSED_MESSAGE, sessionId: data.sessionId, drafts }
      : { type: VOICE_CLOSED_MESSAGE, sessionId: data.sessionId };
  }
  if (data.type === AUTH_REQUEST_MESSAGE) {
    const { nonce } = data as { nonce?: unknown };
    return typeof nonce === 'string' && NONCE_PATTERN.test(nonce)
      ? { type: AUTH_REQUEST_MESSAGE, nonce }
      : null;
  }
  if (data.type === START_CHALLENGE_MESSAGE) {
    const { nonce } = data as { nonce?: unknown };
    return typeof nonce === 'string' && NONCE_PATTERN.test(nonce)
      ? { type: START_CHALLENGE_MESSAGE, nonce }
      : null;
  }
  if (data.type === DICTATION_MESSAGE && typeof data.text === 'string' && data.text.trim()) {
    return { type: DICTATION_MESSAGE, text: data.text };
  }
  return null;
};
